import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Recipe } from '../types';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarProps {
  recipes: Recipe[];
}

interface CookedRecipe {
  id: string;
  cooked_at: string;
  recipes: {
    id: string;
    name: string;
  };
}

export default function Calendar({ recipes }: CalendarProps) {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedRecipe, setSelectedRecipe] = useState<string>('');
  const [cookedRecipes, setCookedRecipes] = useState<CookedRecipe[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  useEffect(() => {
    if (user) {
      loadCookedRecipes();
    }
  }, [user, currentMonth]);

  const loadCookedRecipes = async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);

      const { data, error } = await supabase
        .from('cooked_recipes')
        .select(`
          id,
          cooked_at,
          recipes (
            id,
            name
          )
        `)
        .eq('user_id', user?.id)
        .gte('cooked_at', start.toISOString())
        .lte('cooked_at', end.toISOString())
        .order('cooked_at', { ascending: true });

      if (error) throw error;
      setCookedRecipes(data || []);
    } catch (error) {
      console.error('Error loading cooked recipes:', error);
      toast.error('Failed to load cooking history');
    }
  };

  const handleAddCookedRecipe = async () => {
    if (!user) {
      toast.error('Please sign in to add cooking history');
      return;
    }

    if (selectedDate && selectedRecipe) {
      try {
        const { error } = await supabase
          .from('cooked_recipes')
          .insert({
            recipe_id: selectedRecipe,
            user_id: user.id,
            cooked_at: selectedDate.toISOString(),
          });

        if (error) throw error;

        // Add ingredients to grocery list if the recipe is for today or a past date
        if (!isAfter(startOfDay(selectedDate), startOfDay(new Date()))) {
          const recipe = recipes.find(r => r.id === selectedRecipe);
          if (recipe) {
            const { error: groceryError } = await supabase
              .from('grocery_items')
              .insert(
                recipe.ingredients.map(ingredient => ({
                  name: ingredient.name,
                  amount: ingredient.amount,
                  unit: ingredient.unit,
                  recipe_id: recipe.id,
                  user_id: user.id,
                }))
              );

            if (groceryError) throw groceryError;
          }
        }

        toast.success('Added to cooking history!');
        loadCookedRecipes();
        setSelectedRecipe('');
      } catch (error) {
        console.error('Error adding cooked recipe:', error);
        toast.error('Failed to add to cooking history');
      }
    }
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const getRecipesForDay = (day: Date) => {
    return cookedRecipes.filter(recipe => 
      isSameDay(new Date(recipe.cooked_at), day)
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium">Add Meal to Calendar</h3>
          <div className="flex items-center gap-2">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date || new Date())}
              customInput={
                <button className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50">
                  <CalendarIcon className="h-4 w-4" />
                  {format(selectedDate, 'MMM d, yyyy')}
                </button>
              }
            />
            <select
              value={selectedRecipe}
              onChange={(e) => setSelectedRecipe(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a recipe...</option>
              {recipes.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddCookedRecipe}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add to Calendar
            </button>
          </div>
        </div>

        {/* Calendar View */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentMonth(date => new Date(date.getFullYear(), date.getMonth() - 1))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setCurrentMonth(date => new Date(date.getFullYear(), date.getMonth() + 1))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
            {days.map((day, dayIdx) => {
              const dayRecipes = getRecipesForDay(day);
              const isToday = isSameDay(day, new Date());
              const isPast = isBefore(day, startOfDay(new Date()));

              return (
                <div
                  key={day.toString()}
                  className={`bg-white p-2 min-h-[100px] ${
                    isToday ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="font-medium text-sm mb-1">
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayRecipes.map(recipe => (
                      <div
                        key={recipe.id}
                        className={`text-xs p-1 rounded ${
                          isPast ? 'bg-gray-100 text-gray-600' : 'bg-primary-100 text-primary-700'
                        }`}
                      >
                        {recipe.recipes.name}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}